import React, { useEffect, useState } from "react";
import { Button, PaymentDropdown } from "components/common";
import { useMutation } from "@apollo/react-hooks";
import moment from 'moment';
import { PROCESS_MANUAL_PAYMENT } from "utils";
import {
  ERROR_STATUS,
  EMPTY_STRING,
} from "utils/constants";
import { modalBGColor, modalInputBorderColor } from "utils/customStyles";
import SuccessMessage from "components/Popups/Messages/SuccessMessage";
import commafy from "utils/commafy";
import { SingleDatePicker } from "react-dates";

const sanitizeAmount = (amount) => parseFloat(`${amount}`.replace(/,/g, ""));

const ManualPaymentModal = ({ user, defaultPaymentMethod, refetch }) => {
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethod);
  const [notes, setNotes] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [lastPaymentAttemptAmount, setLastPaymentAttemptAmount] = useState();
  const [processPayment] = useMutation(PROCESS_MANUAL_PAYMENT);
  const [calendarDate, setCalendarDate] = useState(moment());
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarView, setCalendarView] = useState(false);

  useEffect(() => {
    if (notes === EMPTY_STRING) {
      setDisabled(true);
      return;
    }
    setDisabled(!Number(sanitizeAmount(paymentAmount)));
  }, [paymentAmount, notes]);

  const getMethodDetails = (id) =>
    // eslint-disable-next-line
    user.payment_methods.filter(m => m.id == id)[0];

  const formattedDate = () => {
    const d = new Date(Date.now());
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!paymentAmount || !paymentMethod || !notes) {
      setErrorMessage(
        "Please enter a payment amount and payment method and notes. Thank you!",
      );
      return;
    }

    const sanitizedAmount = sanitizeAmount(paymentAmount);
    let idempotency = idempotencyKey;

    if (sanitizedAmount !== lastPaymentAttemptAmount) {
      idempotency = `${Date.now()}${`${Math.random()}`.slice(-6)}`;
      setIdempotencyKey(idempotency);
      setLastPaymentAttemptAmount(sanitizedAmount);
    }

    try {
      const now = moment(calendarDate).isValid() ? calendarDate.isSame(moment(), 'D') : null;
      const res = await processPayment({
        variables: {
          customersEmail: user.email,
          paymentMethod: paymentMethod.uuid,
          idempotencyKey: idempotency,
          amount: sanitizedAmount,
          note: notes,
          now,
          ...(!now && {
            scheduledDate: calendarDate.format('YYYY-MM-DD'),
          }),
        },
      });

      const {
        data: {
          processManualPayment: { status, message },
        },
      } = res;

      if (status === ERROR_STATUS) {
        if (message) {
          setErrorMessage(message);
          setLoading(false);
          return;
        }
        setErrorMessage(
          "There was an error processing the payment. Please try again in 5 minutes.  If this persists, please reach out to dev-support.",
        );
        setLoading(false);
        return;
      }
      setLoading(false);
      setSuccess(true);
      refetch();
    } catch (error) {
      setLoading(false);
      setErrorMessage(
        "Error processing the payment.  Please try again in 5 minutes, or contact dev-support if the issue persists.",
      );
    }
  };

  const WIDTH432 = { width: "432px" };
  const WIDTHANDBORDER = {
    width: "432px",
    border: `1px solid ${modalInputBorderColor}`,
  };

  return (
    <div style={modalBGColor} className="py-8 px-12">
      {success ? (
        <SuccessMessage mainMessage="Payment submitted!" />
      ) : (
        <div
          style={{ width: "434px" }}
          className="flex flex-col justify-center items-center"
        >
          <div className="w-full mb-6 text-2xl">Make a payment</div>
          <div className="w-full font-bold">Payment amount:</div>
          <div
            className="bg-white border border-gray-400 flex mb-4 mt-2 px-3 rounded text-2xl"
            style={WIDTH432}
          >
            <span className="text-gray-500 border-r mr-2 pr-2 text-gray-500 py-1">
              $
            </span>
            <input
              type="text"
              value={paymentAmount}
              className="bg-transparent outline-none w-full text-green-primary ml-1"
              placeholder="0"
              onChange={(e) => {
                setLoading(false);
                setErrorMessage("");
                setPaymentAmount(commafy(e.target.value));
              }}
            />
          </div>
          <div className="w-full font-bold">Payment method:</div>
          <div className="w-full mt-3 mb-4">
            <PaymentDropdown
              paymentMethods={user.payment_methods}
              showBankName
              defaultElementId={defaultPaymentMethod.uuid || null}
              didSelect={(p) => {
                console.log("Make a payment overlay got didSelect:", p);
                setPaymentMethod(p);
              }}
              dropDownClassName="Dropdown-paymentDrawer"
            />
          </div>
          <div style={WIDTH432} className="w-full font-bold">
            When
          </div>
          <div className="w-full flex justify-between">
            {calendarView ? <SingleDatePicker
              numberOfMonths={1}
              date={calendarDate}
              onDateChange={(date) => {
                setCalendarDate(date);
                setShowCalendar(false);
              }}
              focused={showCalendar}
              isDayBlocked={(date) => {
                return [0, 6].includes(date.day());
              }}
              onFocusChange={({ focused }) => setShowCalendar(focused)}
              id="calendar-id"
            /> : <p className="my-3">Today</p>}
            <button
              type="button"
              onClick={() => {
                setShowCalendar(true);
                setCalendarView(true);
              }}
              className="font-semibold bg-green-primary focus:outline-none text-white my-1 px-3"
            >
              Change date
            </button>
          </div>

          <div className="text-xs text-gray-600 text-justify pt-4">
            <div>
              {`I understand you authorize TomoCredit to ACH debit your ${
                getMethodDetails(paymentMethod.id).institution
              } Account ending in  (...${
                getMethodDetails(paymentMethod.id).account_number_last_four
              }) for the amount of $${paymentAmount} on ${calendarView ? moment(calendarDate).format('MM/DD/YYYY') : formattedDate()}. Is this information correction and do we have your authorization? You will be receiving an email confirmation and if you have any questions about your payment, you must contact us 3 business days beforehand at 866.432.7695`}
            </div>
          </div>
          <div style={WIDTH432} className="w-full font-bold">
            Notes
          </div>
          <textarea
            style={WIDTHANDBORDER}
            onChange={(e) => {
              setLoading(false);
              setErrorMessage("");
              setNotes(e.target.value);
            }}
          />
          <div className={errorMessage ? "text-red-600 mb-4" : "hidden"}>
            {errorMessage}
          </div>
          <Button
            className={`mt-5 text-2xl btn-green ${
              loading || disabled ? "opacity-50" : ""
            }`}
            disabled={loading || disabled || !moment(calendarDate).isValid()}
            style={WIDTH432}
            onClick={() => {
              handleSubmit();
            }}
          >
            Submit
          </Button>
          <div className="text-xs text-gray-600 text-justify pt-4">
            <div>
              {`By clicking “Submit” you are authorizing us to initiate a debit on your behalf, from your ${
                getMethodDetails(paymentMethod.id).institution
              } Account (...${
                getMethodDetails(paymentMethod.id).account_number_last_four
              }) for $${paymentAmount} on ${calendarView ? moment(calendarDate).format('MM/DD/YYYY') : formattedDate()}. You agree that you will not dispute this transaction, so long as the transaction corresponds to the terms of this authorization.`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualPaymentModal;
